// ─────────────────────────────────────────────────────────────────────────────
// stores/userStore.ts – Firestore users collection
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  collection, doc,
  setDoc, updateDoc, deleteDoc,
  serverTimestamp, type QuerySnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db, callable } from '@/firebase/config';
import { liveAbo } from '@/utils/liveAbo';
import { useAuthStore } from './authStore';
import type { AppUser } from '@/firebase/models';

export type { AppUser };

export interface UserProfile {
  id:          string;   // = Firebase Auth UID
  email:       string;
  displayName: string;
  role:        'admin' | 'user';
  notiz?:      string;
  disabled?:   boolean;
  createdAt?:  unknown;
  updatedAt?:  unknown;
}

export const useUserStore = defineStore('users', () => {
  const users     = ref<UserProfile[]>([]);
  const isLoading = ref(false);
  /**
   * DER BENANNTE SONDERFALL DIESES STORES (K1, REQ-OPS-053 (a) (ii)).
   *
   * Bis zum 2026-08-18 lautete der Fehler-Rueckruf hier `() => { isLoading =
   * false; }` — er war nicht ABWESEND, sondern STUMM, und das ist die
   * schlechtere Lage: nach dem Tod des Abos sah die Liste aus wie eine fertig
   * geladene, LEERE. Ein Verwalter haette daraus geschlossen, es gebe keine
   * Benutzer. Seither traegt der Store beide Merkmale nach aussen und geht den
   * gemeinsamen Weg (`liveAbo`).
   */
  const error        = ref<string | null>(null);
  const isSubscribed = ref(false);
  let   unsub: Unsubscribe | null = null;

  // ── Subscribe ────────────────────────────────────────────────
  /**
   * Die Benutzerliste — **NUR FUER EINEN ADMIN** (T-M30.7, REQ-SEC-027 (c),
   * REQ-SEC-029 (a)/(e)).
   *
   * Die Rules erlauben `users` einem `user` allein als `get` auf das EIGENE
   * Dokument, nie als Liste. Ein Abo waere deshalb bei jedem Kundenbenutzer ein
   * sicheres `permission-denied` — fuenfmal wiederaufgenommen, mit einer
   * Fehlermeldung im Rahmen-Hinweis, obwohl nichts gestoert ist. **Der Store
   * abonniert gar nicht erst**; die Liste bleibt leer, ohne Fehlzustand.
   *
   * *Das ist Bequemlichkeit und keine Sperre: durchgesetzt wird in den Rules
   * (REQ-SEC-029). Die Ansicht selbst ist zusaetzlich ueber `meta.requiresAdmin`
   * verschlossen (`router/index.ts`).*
   */
  function subscribe() {
    if (!useAuthStore().isAdmin) {
      unsub?.();
      unsub              = null;
      users.value        = [];
      isLoading.value    = false;
      error.value        = null;
      isSubscribed.value = false;
      return;
    }
    // Bis zum 2026-08-18 stand hier `if (unsub) return;` — ein Waechter gegen
    // das doppelte Abo, der zugleich jede WIEDERAUFNAHME von Hand verhinderte:
    // nach erschoepfter Staffel bliebe der Store fuer immer tot. Statt dessen
    // dieselbe Bauform wie in den uebrigen Stores: die vorige Anmeldung wird
    // vor dem Neuabonnieren abgeraeumt (und damit eine laufende Staffel
    // abgebrochen, REQ-OPS-053 (d)).
    unsub?.();
    unsub = null;
    isLoading.value = true;
    error.value     = null;
    isSubscribed.value = true;
    unsub = liveAbo<QuerySnapshot>({
      collection: 'users',
      ziel: () => collection(db, 'users'),
      onData: (snap) => {
        users.value = snap.docs.map(d => ({
          id:          d.id,
          email:       d.data().email        ?? '',
          displayName: d.data().displayName  ?? d.data().email ?? '',
          role:        d.data().role         ?? 'user',
          notiz:       d.data().notiz        ?? '',
          disabled:    d.data().disabled     ?? false,
          createdAt:   d.data().createdAt,
          updatedAt:   d.data().updatedAt,
        }));
        isLoading.value = false;
      },
      onFehler: (err) => {
        error.value        = err.message;
        isLoading.value    = false;
        isSubscribed.value = false;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung): sonst bliebe hier die
      // Meldung stehen, waehrend die Liste laengst wieder live ist — die
      // Falschaussage aus K1 mit umgekehrtem Vorzeichen.
      onErholt: () => {
        error.value        = null;
        isSubscribed.value = true;
      },
      onStill: () => {
        isLoading.value    = false;
        isSubscribed.value = false;
      },
    });
  }

  function unsubscribe() {
    unsub?.(); unsub = null;
    isSubscribed.value = false;
  }

  // ─────────────────────────────────────────────────────────────
  // DIE DREI SCHREIBWEGE AUF `users` — DIE BEDIENSTELLE DES ADMINS
  // (REQ-SEC-029 (b) Praez. 1 vom 2026-09-01, REQ-SEC-027 (a))
  //
  // ⚠ **SIE BLEIBEN, UND DAS IST ENTSCHIEDEN.** Die Matrix aus REQ-SEC-029 (a)
  // sagt fuer `users` „nur Admin" — nicht „niemand": ein Admin fuehrt die
  // Benutzerverwaltung samt „Rolle setzen", und REQ-SEC-030 baut dafuer KEINE
  // Cloud Function; der einzige Weg ist dieser Client.
  //
  // **Was mit T-M30.7 gefallen ist, ist der SELBST-Schreibweg**: der
  // Erstlogin-Guard in `stores/authStore.ts`, der beim ersten Anmelden das
  // EIGENE `users`-Dokument anlegte. Die Selbst-Befoerderung ist damit
  // strukturell unmoeglich — hier schreibt ein Admin ueber ANDERE, dort schrieb
  // jeder ueber sich selbst.
  // ─────────────────────────────────────────────────────────────

  // ── Update (existing user – profile only) ────────────────────
  async function updateUser(id: string, data: Partial<Omit<UserProfile, 'id'>>) {
    await updateDoc(doc(db, 'users', id), { ...data, updatedAt: serverTimestamp() });
  }

  // ── Create profile document ──────────────────────────────────
  async function createUserProfile(uid: string, data: Omit<UserProfile, 'id'>) {
    await setDoc(doc(db, 'users', uid), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  // ── Delete profile document ──────────────────────────────────
  async function deleteUser(id: string) {
    await deleteDoc(doc(db, 'users', id));
  }

  // ─────────────────────────────────────────────────────────────
  // DIE EINLADUNG (REQ-SEC-030 (a)/(e)/(h), REQ-DM-071)
  //
  // ⚠ **DER RUMPF HAT SICH AM 2026-09-01 GEAENDERT (T-M30.6), UND DIE
  // AENDERUNG IST NICHT KOSMETISCH — berichtigt statt geloescht
  // (REQ-OPS-036 Praez. 6).**
  //
  // **BIS ZUM 2026-09-01** sendete diese Stelle `{ email, displayName, role }`
  // — **ohne `companyIds`** — und der Kommentar darueber lautete: „The Cloud
  // Function (inviteuser) creates the Firebase Auth account, generates a
  // password-reset link, and sends a branded invitation email via the
  // configured SMTP server (with Firebase as automatic fallback)."
  //
  // **BEIDES TRIFFT NICHT MEHR ZU.** Seit T-M30.4 legt `inviteuser` KEIN Konto
  // mehr an — es entsteht erst bei der Annahme (REQ-SEC-030 (b)) — und es
  // verlangt **mindestens einen Mandanten** (REQ-DM-071); ein Aufruf ohne
  // `companyIds` wird mit 400 abgewiesen. *Das ist die Auflage E1 am
  // Rollout-Task T-M30.12: zwischen dem Functions- und dem Hosting-Schritt ist
  // die Einladung unbedienbar, und zwar genau wegen dieser Zeile.*
  //
  // ⚠ **DER ZWEITE HALBSATZ DARUEBER GILT SEIT DEM 2026-09-15 NUR NOCH FUER DIE
  // ROLLE `user` — berichtigt statt geloescht (REQ-OPS-036 Praez. 6).** Der
  // aufgehobene Wortlaut ist der oben stehende: „es verlangt **mindestens
  // einen Mandanten** (REQ-DM-071); ein Aufruf ohne `companyIds` wird mit 400
  // abgewiesen." **WAS STATTDESSEN GILT** (Entscheid des Auftraggebers vom
  // 2026-09-15, REQ-SEC-030 Praez. 1 (a), REQ-DM-071 Praez. 1): `inviteuser`
  // weist die **leere** Mandantenliste weiterhin mit 400 und buchstabengleich
  // demselben Text ab — **aber nur, wenn die Rolle nicht `admin` ist**. Fuer
  // einen Admin ist `companyIds: []` eine gueltige Angabe; die Rolle ist global
  // (REQ-SEC-027 (a)), `isAdmin()` verlangt in den Rules keine Mitgliedschaft.
  // **Das Feld selbst bleibt Pflicht im Rumpf** — es wird IMMER gesendet, im
  // Admin-Fall als leere Liste, nie weggelassen (REQ-DM-071 Praez. 1 (1)/(2)).
  // *Der erste Halbsatz — kein Konto vor der Annahme — ist unberuehrt.*
  //
  // **ZWEI AUSGAENGE, und der Aufrufer muss sie unterscheiden koennen:**
  //   • `existingUser: false` — eine Einladung ist angelegt oder erneuert
  //     worden und unterwegs; `expiresAt` traegt die Frist.
  //   • `existingUser: true`  — die Adresse hat bereits ein Konto. Dann fuegt
  //     die Cloud die Mitgliedschaften hinzu und benachrichtigt; **es entsteht
  //     keine Einladung, kein zweites Konto und kein zweites Passwort**
  //     (REQ-SEC-030 (h)).
  // ─────────────────────────────────────────────────────────────

  /** Die Antwort von `inviteuser` — beide Ausgaenge in EINEM Typ. */
  interface EinladungsAntwort {
    success:      boolean;
    sentTo:       string;
    existingUser: boolean;
    /** Nur bei `existingUser: true`. */
    userId?:      string;
    /** Nur bei `existingUser: false`. */
    invitationId?: string;
    renewed?:      boolean;
    /** ISO-8601, nur bei `existingUser: false`. */
    expiresAt?:    string;
  }

  async function inviteUser(opts: {
    email:       string;
    displayName: string;
    role:        'admin' | 'user';
    /**
     * **Das FELD ist Pflicht, sein INHALT nur bei der Rolle `user`**
     * (REQ-DM-071 Praez. 1): mindestens ein Mandant, in den eingeladen wird —
     * bei der Rolle `admin` ist die **leere Liste** zulaessig und wird
     * trotzdem gesendet (nie `null`, nie weggelassen).
     *
     * ⚠ Bis zum 2026-09-15 lautete diese Zeile „Pflicht (REQ-DM-071):
     * mindestens ein Mandant, in den eingeladen wird." — ohne Rollenvorbehalt.
     * *Berichtigt statt geloescht (REQ-OPS-036 Praez. 6).*
     */
    companyIds:  string[];
  }): Promise<EinladungsAntwort> {
    return await callable<EinladungsAntwort>('inviteuser', {
      email:       opts.email,
      displayName: opts.displayName,
      role:        opts.role,
      companyIds:  opts.companyIds,
    });
  }

  return {
    users, isLoading, error, isSubscribed,
    subscribe, unsubscribe,
    updateUser, createUserProfile, deleteUser,
    inviteUser,
  };
});
